import { motion } from "framer-motion";
import { MapPin, Navigation, Phone, Clock } from "lucide-react";
import { useLang } from "@/i18n";

const QUERY = encodeURIComponent("MAHIMA METALS C429+FXV Kim Gujarat 394112");
const MAP_EMBED = `https://maps.google.com/maps?q=${QUERY}&z=16&output=embed`;
const DIRECTIONS = `https://www.google.com/maps/dir/?api=1&destination=${QUERY}`;
const MAPS_LINK = "https://maps.app.goo.gl/7TVDddy1w2onuZLW7";

export default function LocationSection() {
  const { str } = useLang();
  return (
    <section
      id="location"
      data-testid="location-section"
      className="relative px-6 sm:px-10 lg:px-16 py-24 lg:py-32 border-b border-white/10"
    >
      <p className="font-ticker text-[11px] tracking-[0.4em] uppercase text-[#25D366] mb-4">
        {str.locTag}
      </p>
      <h2 className="font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-none mb-12 lg:mb-16">
        {str.locH1} <span className="text-stroke-gold">{str.locH2}</span>
      </h2>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-3 border border-white/10 bg-[#121212] overflow-hidden"
        >
          <iframe
            title="Mahima Metals Location"
            data-testid="location-map-iframe"
            src={MAP_EMBED}
            className="w-full h-[380px] lg:h-[460px] block"
            style={{ border: 0, filter: "invert(90%) hue-rotate(180deg) grayscale(25%) contrast(0.95)" }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-2 border border-white/10 bg-[#0F0F0F] p-8 sm:p-10 flex flex-col justify-between gap-8"
        >
          <div className="space-y-7">
            <div className="flex items-start gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center border border-[#25D366]/50 text-[#25D366]">
                <MapPin size={18} />
              </span>
              <div>
                <p className="font-ticker text-[10px] tracking-[0.3em] uppercase text-white/40 mb-1">{str.address}</p>
                <p data-testid="location-address" className="font-display font-extrabold uppercase tracking-tight text-xl">
                  Mahima Metals
                </p>
                <p className="text-sm text-white/55 mt-1">C429+FXV, Kim, Gujarat 394112, India</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center border border-[#25D366]/50 text-[#25D366]">
                <Clock size={18} />
              </span>
              <div>
                <p className="font-ticker text-[10px] tracking-[0.3em] uppercase text-white/40 mb-1">{str.timing}</p>
                <p className="text-sm text-white/70">{str.timingVal}</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center border border-[#25D366]/50 text-[#25D366]">
                <Phone size={18} />
              </span>
              <div>
                <p className="font-ticker text-[10px] tracking-[0.3em] uppercase text-white/40 mb-1">{str.call}</p>
                <a href="tel:+918000093073" data-testid="location-phone-link" className="text-sm font-semibold text-[#25D366] hover:text-[#7BE3A6] transition-colors">
                  +91 80000 93073
                </a>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <a
              href={DIRECTIONS}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="location-directions-btn"
              className="inline-flex items-center justify-center gap-3 bg-[#25D366] text-[#0A0A0A] px-7 py-4 font-ticker text-[11px] tracking-[0.25em] uppercase font-semibold hover:bg-[#7BE3A6] transition-colors duration-300"
            >
              <Navigation size={15} /> {str.directions}
            </a>
            <a
              href={MAPS_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="location-open-maps-btn"
              className="inline-flex items-center justify-center gap-3 border border-white/20 px-7 py-4 font-ticker text-[11px] tracking-[0.25em] uppercase text-white/75 hover:border-[#25D366] hover:text-[#25D366] transition-colors duration-300"
            >
              <MapPin size={15} /> {str.openMaps}
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
